(() => {
  'use strict';

  const initialized = new WeakSet();
  const SELECTED_CLASS = 'subscription__box--selected';

  const setText = (node, value) => {
    if (node && node.textContent.trim() !== value) node.textContent = value;
  };

  const init = (marker) => {
    if (!marker || initialized.has(marker)) return;
    const root = marker.closest('[id^="shopify-section-"]');
    const matrixNode = marker.previousElementSibling;
    if (!root || !matrixNode?.matches('[data-hxrb-price-matrix]')) return;

    let prices;
    try {
      prices = JSON.parse(matrixNode.textContent);
    } catch (error) {
      console.error('Rhodiola bundle price matrix is invalid.', error);
      return;
    }
    if (!Array.isArray(prices) || prices.length === 0) return;

    initialized.add(marker);
    const byId = new Map(prices.map((item) => [String(item.id), item]));
    const byTitle = new Map(prices.map((item) => [item.title, item]));
    const params = new URLSearchParams(window.location.search);
    const explicitVariant = byId.get(params.get('variant'));
    const defaultVariant = byTitle.get('Buy 2 Get 1 Free') || prices[0];
    let selected = explicitVariant || defaultVariant;
    let subscriptionSelected = true;
    let busy = false;
    let applying = false;
    let applyQueued = false;

    const getForm = () => root.querySelector('form[data-type="add-to-cart-form"]');
    const getButton = () => getForm()?.querySelector('.product-form__submit');
    const getSubscriptionBox = () => root.querySelector('.subscription__box--subscription');

    const scrollOfferIntoView = () => {
      const tiers = root.querySelector('.quantity-breaks-container');
      const button = getButton();
      if (!tiers || !button) return;

      const viewportPadding = 12;
      const topOverlayBottom = Array.from(
        document.querySelectorAll('#countdown-banner, .section-header, .header-wrapper'),
      ).reduce((bottom, node) => {
        const rect = node.getBoundingClientRect();
        return rect.top <= 1 && rect.bottom > 0 ? Math.max(bottom, rect.bottom) : bottom;
      }, 0);
      const visibleTop = topOverlayBottom + viewportPadding;
      const tiersRect = tiers.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const frameHeight = buttonRect.bottom - tiersRect.top;
      const availableHeight = window.innerHeight - visibleTop - viewportPadding;
      const frameTop = window.scrollY + tiersRect.top;

      // Keep the complete tier ladder and its primary action in one viewport.
      // If an unusually short viewport cannot contain the frame, preserve the
      // primary action at the bottom instead of letting the fixed header cover it.
      const targetTop = frameHeight <= availableHeight
        ? frameTop - visibleTop
        : window.scrollY + buttonRect.bottom - window.innerHeight + viewportPadding;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    const reflectSubscriptionBox = () => {
      const box = getSubscriptionBox();
      if (!box) return;
      const selectedValue = subscriptionSelected ? 'true' : 'false';
      box.classList.toggle(SELECTED_CLASS, subscriptionSelected);
      // This attribute is observed below. Rewriting the same value creates a
      // new MutationRecord and can starve first paint in a microtask loop.
      if (box.dataset.selected !== selectedValue) box.dataset.selected = selectedValue;
      box.setAttribute('role', 'checkbox');
      box.setAttribute('tabindex', '0');
      box.setAttribute('aria-label', 'Save 25% With Automatic Refills');
      box.setAttribute('aria-checked', selectedValue);
    };

    const syncForm = () => {
      const form = getForm();
      if (!form || !selected) return;
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = String(selected.id);

      let planInput = form.querySelector('input[data-hxrb-selling-plan]');
      if (!planInput) {
        planInput = document.createElement('input');
        planInput.type = 'hidden';
        planInput.name = 'selling_plan';
        planInput.dataset.hxrbSellingPlan = 'true';
        form.append(planInput);
      }
      planInput.value = subscriptionSelected ? String(selected.planId || '') : '';
      planInput.disabled = !subscriptionSelected || !selected.planId;
    };

    const syncTierCards = () => {
      root.querySelectorAll('.quantity-breaks-container input[type="radio"]').forEach((input) => {
        const item = byTitle.get(input.value);
        const card = input.nextElementSibling;
        if (!item || !card?.classList.contains('quantity-break')) return;
        input.checked = String(item.id) === String(selected.id);

        const price = subscriptionSelected ? item.subscriptionPriceMoney : item.priceMoney;
        const compare = item.compareMoney; // OXY-COMPARE-AT-PRESERVE-20260713
        setText(card.querySelector('.quantity-break__price'), price);
        const captionNode = card.querySelector('.quantity-break__caption');
        setText(captionNode, '');
        if (captionNode) captionNode.hidden = true;
        const compareNode = card.querySelector('.quantity-break__compare-price');
        setText(compareNode, compare);
        compareNode?.classList.toggle('hidden', !compare || compare === price);
      });
    };

    const applyState = ({ reflectBox = true, updateUrl = false } = {}) => {
      if (!selected || applying) return;
      applying = true;
      try {
        syncTierCards();
        syncForm();
        if (reflectBox) reflectSubscriptionBox();
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(selected.id));
          window.history.replaceState({}, '', url);
        }
      } finally {
        applying = false;
      }
    };

    const resetCheckoutState = () => {
      busy = false;
      const button = getButton();
      if (!button) return;
      button.disabled = false;
      button.classList.remove('hxrb-checkout-processing');
      button.removeAttribute('aria-busy');
      setText(button.querySelector('.main-atc__label__text'), 'ADD TO CART');
    };

    const queueApply = () => {
      if (applyQueued || applying) return;
      applyQueued = true;
      queueMicrotask(() => {
        applyQueued = false;
        applyState();
      });
    };

    root.addEventListener(
      'click',
      (event) => {
        const stickyButton = event.target.closest('button.sticky-atc__scroll-btn');
        if (stickyButton) {
          event.preventDefault();
          event.stopImmediatePropagation();
          scrollOfferIntoView();
          return;
        }

        const tier = event.target.closest('label.quantity-break');
        if (tier && root.contains(tier)) {
          const input = tier.previousElementSibling;
          const item = input?.matches('input[type="radio"]') ? byTitle.get(input.value) : null;
          if (item) {
            selected = item;
            applyState({ updateUrl: true });
          }
          return;
        }

        const box = getSubscriptionBox();
        if (box && box.contains(event.target)) {
          subscriptionSelected = !subscriptionSelected;
          applyState({ reflectBox: false });
          setTimeout(() => applyState(), 0);
        }
      },
      true,
    );

    root.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest('.quantity-breaks-container input[type="radio"]');
        const item = input ? byTitle.get(input.value) : null;
        if (!item) return;
        selected = item;
        applyState({ updateUrl: true });
      },
      true,
    );

    root.addEventListener('keydown', (event) => {
      const box = getSubscriptionBox();
      if (box && box.contains(event.target) && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        box.click();
      }
    });

    root.addEventListener(
      'submit',
      async (event) => {
        const form = getForm();
        if (!form || event.target !== form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (busy || !selected) return;

        busy = true;
        const button = getButton();
        const label = button?.querySelector('.main-atc__label__text');
        const originalLabel = label?.textContent || 'ADD TO CART';
        const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
        const errorText = form.querySelector('.product-form__error-message');
        if (errorWrapper) errorWrapper.hidden = true;
        if (button) {
          button.disabled = true;
          button.classList.add('hxrb-checkout-processing');
          button.setAttribute('aria-busy', 'true');
        }
        setText(label, 'PROCESSING...');

        try {
          const data = new FormData(form);
          data.set('id', String(selected.id));
          data.delete('selling_plan');
          if (subscriptionSelected && selected.planId) {
            data.set('selling_plan', String(selected.planId));
          }
          const rootPath = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootPath}cart/add.js?kaching_bundles=true`, {
            method: 'POST',
            body: data,
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this offer.');
          window.location.assign(`${rootPath}checkout`);
        } catch (error) {
          busy = false;
          if (button) {
            button.disabled = false;
            button.classList.remove('hxrb-checkout-processing');
            button.removeAttribute('aria-busy');
          }
          setText(label, originalLabel.trim());
          if (errorText) errorText.textContent = error.message;
          if (errorWrapper) errorWrapper.hidden = false;
        }
      },
      true,
    );

    // iOS Safari can restore this page from the back-forward cache with the
    // pre-navigation button DOM intact. Reset before caching and again when
    // the page is shown so returning from checkout is immediately actionable.
    window.addEventListener('pagehide', resetCheckoutState);
    window.addEventListener('pageshow', resetCheckoutState);

    const observer = new MutationObserver(queueApply);
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'data-selected'],
    });

    const currentBox = getSubscriptionBox();
    subscriptionSelected = currentBox
      ? currentBox.classList.contains(SELECTED_CLASS) || currentBox.dataset.selected === 'true'
      : true;

    if (!explicitVariant) {
      const defaultInput = [...root.querySelectorAll('.quantity-breaks-container input[type="radio"]')].find(
        (input) => input.value === defaultVariant.title,
      );
      if (defaultInput && !defaultInput.checked) defaultInput.click();
    }
    applyState();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-hxrb-native-bundles]').forEach(init);
  };

  const start = () => initAll();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
